import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  PhoneCall,
  Mail,
  RefreshCw,
  Flame,
  TrendingUp,
  TrendingDown,
  Minus,
  MapPin,
  ChevronDown,
  ChevronUp,
  Sparkles,
  BellRing,
  StickyNote,
  CalendarClock,
  Target,
  SlidersHorizontal,
  MessageSquare,
  Settings,
  Search,
  X,
  Zap,
} from 'lucide-react';
import {
  fetchSmartCallList,
  markBuyerContacted,
  removeFromCallList,
  fetchCallListSettings,
  updateCallListSettings,
} from '../../analytics/api/analyticsApi';
import { resolveContactIdentity } from '../../../api/contact';
import ContactNotesButton from '../../contacts/components/ContactNotesButton';
import ContactLink from '../../contacts/components/ContactLink';
import CalledButton from '../../../components/properties/CalledButton';
import ContactBuyerPreferencesEditor from '../../contacts/components/ContactBuyerPreferencesEditor';
import BuyerStatusActions from '../../contacts/components/BuyerStatusActions';
import { hasAdminAccess } from "../../../utils/roleAccess";

/**
 * "Who to call" — a ranked, activity-driven call list on the agent dashboard.
 *
 * Reads buyer-portal analytics and tells the agent which buyers to phone and
 * why, straight from what each buyer did on the portal. Agents see buyers on
 * their own listings (first/second contact) + referrals. Admins get a switcher:
 * "Active buyers" (default) is the freshest office-wide list of everyone moving
 * on the portal, while "Unassigned" is the residual pool no agent owns — which
 * reads stale by design, since hot buyers get attributed to their agent and
 * skimmed off it. If an agent has nobody attributed yet, the BE falls back to
 * active portal buyers, then to a random handful — so the panel is never empty
 * (data.fallback says which tier is shown).
 *
 * Marking a buyer contacted drops them off the list. By default they only
 * resurface if they're active on the portal again (activity newer than the
 * contacted timestamp) — so a buyer who goes quiet would never come back. To
 * cover that, "Mark contacted" lets the agent set a call-back reminder in the
 * same step; the reminder re-adds the buyer to the dedicated "Call-backs due"
 * section once its date arrives, regardless of portal activity.
 *
 * The gear next to Refresh opens each agent's own settings for which of their
 * listings feed the list (lead agent, second agent, both or neither), because
 * teams are set up differently: on some, only the lead agent works the buyer
 * calls, on others the second agent does all of it.
 */

const PRIORITY_STYLES = {
  hot: {
    label: 'Hot',
    chip: 'bg-red-100 text-red-600',
    bar: 'bg-red-500',
    icon: Flame,
    ring: 'border-red-100',
  },
  warm: {
    label: 'Warm',
    chip: 'bg-orange-100 text-orange-600',
    bar: 'bg-orange-400',
    icon: TrendingUp,
    ring: 'border-orange-100',
  },
  cold: {
    label: 'Watch',
    chip: 'bg-sky-100 text-sky-600',
    bar: 'bg-sky-400',
    icon: TrendingUp,
    ring: 'border-sky-100',
  },
};

// Buyer Intent Score category → colour (mirrors the analytics page bands).
const INTENT_CATEGORY_STYLES = {
  priority: 'bg-red-50 text-red-700',
  hot: 'bg-orange-50 text-orange-700',
  active: 'bg-amber-50 text-amber-700',
  warm: 'bg-sky-50 text-sky-700',
  cold: 'bg-gray-100 text-gray-600',
};

const FINANCE_CHIP_COLORS = {
  Cash: '#16a34a',
  'Pre-approved': '#0d9488',
  Broker: '#7c3aed',
  'Not ready': '#9ca3af',
};

// Open-home feedback interest chip (matches the buyer's interest_level).
const FEEDBACK_CHIP = {
  hot: 'bg-orange-100 text-orange-700',
  warm: 'bg-sky-100 text-sky-700',
  cold: 'bg-gray-100 text-gray-500',
  none: 'bg-gray-100 text-gray-500',
};
const FEEDBACK_LABEL = {
  hot: 'Very interested',
  warm: 'Interested',
  cold: 'Lukewarm',
  none: 'Not interested',
};

/** Compact week-over-week trend indicator (↑ +N / ↓ -N / – 0). */
const IntentTrend = ({ trend }) => {
  if (trend > 0) {
    return <span className="inline-flex items-center gap-0.5 text-green-600 font-semibold"><TrendingUp className="w-3 h-3" />+{trend}</span>;
  }
  if (trend < 0) {
    return <span className="inline-flex items-center gap-0.5 text-red-500 font-semibold"><TrendingDown className="w-3 h-3" />{trend}</span>;
  }
  return <span className="inline-flex items-center gap-0.5 text-gray-400"><Minus className="w-3 h-3" />0</span>;
};

// A card may be activity-driven (buyer_id), reminder-driven (reminder_id) or
// open-home-driven (attendee_id) — the last two often have no portal account at
// all, so build a stable key that works for every case.
const cardKey = (b) =>
  b.buyer_id != null
    ? `b${b.buyer_id}`
    : b.contact_id != null
    ? `c${b.contact_id}`
    : b.attendee_id != null
    ? `a${b.attendee_id}`
    : `r${b.reminder_id}`;

// A Meta (Facebook) lead nobody has phoned yet. These are pinned to the very top
// of the list, above call-backs and open homes, because the enquiry goes cold
// within hours (Monday 13234112853). A Meta lead always wins the section, even
// when the same person also has a reminder or open-home feedback.
const isMetaLeadCard = (b) => !!b.meta_lead;

// A card belongs in the "Call-backs due" section when it carries a due reminder
// (boosted activity card) or is a reminder-only entry.
const isReminderCard = (b) => !isMetaLeadCard(b) && (!!b.has_reminder || b.source === 'reminder');

// A card belongs in the "From your open homes" section when it came from an
// inspection attendee the agent marked hot/warm (or wrote feedback about).
// These get their own section for the same reason reminders do: their score is
// a human judgement on a small scale, so left in the activity ranking they'd sit
// below every heavy portal browser — which is exactly how open-home interest
// went unnoticed before.
const isOpenHomeCard = (b) => !isMetaLeadCard(b) && !isReminderCard(b) && b.attendee_id != null;

const OH_PRIORITY_RANK = { hot: 2, warm: 1, cold: 0 };
// Hot-marked attendees lead, then warm, then by score — so the buyer the agent
// personally flagged at the inspection is the first name they see.
const byOpenHomeInterest = (a, b) =>
  (OH_PRIORITY_RANK[b.priority] ?? 0) - (OH_PRIORITY_RANK[a.priority] ?? 0) ||
  (b.call_score || 0) - (a.call_score || 0);

/**
 * Render a reason line, emphasising the property address within it when the BE
 * resolved one (e.g. "Ran investment numbers — $290k ... likely on <address>").
 * The address is bolded and tinted with a pin so the agent can see at a glance
 * which listing the buyer was pricing.
 */
const renderReason = (reason, property) => {
  if (!property || !reason.includes(property)) return reason;
  const [before, after] = reason.split(property);
  return (
    <>
      {before}
      <span className="font-semibold text-teal-700 inline-flex items-baseline gap-0.5">
        <MapPin className="w-3 h-3 self-center flex-shrink-0" />
        {property}
      </span>
      {after}
    </>
  );
};

/**
 * One ranked call-list card. The green Called button is the same one as the
 * property tabs (CalledButton): one tap logs the call with an outcome ("no
 * answer", "left voicemail", ...) or sets a quick call-back, which both logs a
 * proper call (note, last-contacted stamp, a typed call for the agent KPIs, and
 * the call-back reminder) and drops the card off the list.
 */
const CallCard = ({ b, expanded, onToggle, onNoteChange, onContacted }) => {
  const [busy, setBusy] = useState(false);
  // The call log and the preferences editor attach to a CRM Contact; reminder-only
  // cards already carry one, activity cards are resolved (get-or-create) on demand.
  const [contactId, setContactId] = useState(b.contact_id || null);
  const [resolvingContact, setResolvingContact] = useState(false);
  // Inline "edit buying preferences" modal — reuses the full portal-mirror editor
  // so agents can tidy a buyer's preferences (suburbs, types, budget) mid-call.
  const [prefsOpen, setPrefsOpen] = useState(false);
  // Save Preferences also saves a status picked in the modal (CRM-2026-00134).
  const statusRef = useRef(null);
  // "Remove from list" asks once inline before hiding the card.
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removeError, setRemoveError] = useState(false);

  const style = PRIORITY_STYLES[b.priority] || PRIORITY_STYLES.cold;
  const PriorityIcon = style.icon;
  const key = cardKey(b);
  const reasons = b.reasons || [];
  const visibleReasons = expanded ? reasons : reasons.slice(0, 2);
  const identity = { buyerId: b.buyer_id, contactId: b.contact_id, email: b.email, name: b.name, phone: b.phone };

  // Log the call (outcome + optional call-back) and drop the card off the list.
  // Buyer / activity / reminder cards need a resolved Contact so the call is
  // logged against them; attendee and meta-lead cards are resolved server-side
  // from their own record. A failure to resolve still lets the dismissal through.
  const handleCalled = useCallback(async ({ outcome, remindInDays }) => {
    setBusy(true);
    let cid = contactId;
    if (!cid && b.attendee_id == null && !(b.meta_lead && b.lead_id)) {
      try {
        const res = await resolveContactIdentity(identity);
        cid = res.data?.contact_id || null;
        if (cid) setContactId(cid);
      } catch (e) {
        // Fall through — the dismissal below still works without a contact id.
      }
    }
    try {
      await markBuyerContacted({
        buyerId: b.buyer_id,
        reminderId: b.reminder_id,
        attendeeId: b.attendee_id,
        leadId: b.meta_lead ? b.lead_id : undefined,
        contactId: cid || undefined,
        outcome,
        remindInDays: remindInDays || undefined,
      });
      onContacted(key);
    } catch (e) {
      setBusy(false); // keep the card on failure
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, b.buyer_id, b.reminder_id, b.attendee_id, b.meta_lead, b.lead_id, key, onContacted]);

  // Take the card off the list without logging a call or setting a call-back,
  // for people who simply don't need one (Paul Jimoulis, CRM-2026-00122).
  const handleRemove = useCallback(async () => {
    setBusy(true);
    setRemoveError(false);
    try {
      await removeFromCallList({
        buyerId: b.buyer_id,
        reminderId: b.reminder_id,
        attendeeId: b.attendee_id,
      });
      onContacted(key);
    } catch (e) {
      setBusy(false); // keep the card on failure
      setRemoveError(true);
    }
  }, [b.buyer_id, b.reminder_id, b.attendee_id, key, onContacted]);

  // Open the buying-preferences editor, resolving (get-or-create) a CRM Contact
  // to attach the preferences to — the editor writes through to the buyer's
  // portal preferences and our buyer-trend stats.
  const openPrefs = useCallback(async () => {
    setPrefsOpen(true);
    if (contactId) return;
    setResolvingContact(true);
    try {
      const res = await resolveContactIdentity(identity);
      setContactId(res.data?.contact_id || null);
    } catch (e) {
      // The editor shows a "couldn't open" state if this stayed null.
    } finally {
      setResolvingContact(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  return (
    <div
      className={`rounded-lg border ${isMetaLeadCard(b) ? 'border-blue-300 ring-1 ring-blue-100' : isReminderCard(b) ? 'border-amber-200' : style.ring} bg-white hover:shadow-md transition-all`}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <ContactLink
                identity={identity}
                className="font-bold text-sm text-gray-900 truncate hover:text-[#3a99a3] hover:underline transition-colors"
              >
                {b.name}
              </ContactLink>
              {isMetaLeadCard(b) && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide flex items-center gap-1 bg-blue-100 text-blue-700">
                  <Zap className="w-2.5 h-2.5" />
                  Facebook lead
                </span>
              )}
              {b.has_reminder && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide flex items-center gap-1 bg-amber-100 text-amber-700">
                  <BellRing className="w-2.5 h-2.5" />
                  {b.reminder_overdue ? 'Overdue' : 'Reminder'}
                </span>
              )}
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide flex items-center gap-1 ${style.chip}`}>
                <PriorityIcon className="w-2.5 h-2.5" />
                {style.label}
              </span>
              {b.likely_transaction && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide flex items-center gap-1 bg-emerald-100 text-emerald-700" title="Likely to buy in the next 90 days">
                  <Target className="w-2.5 h-2.5" />
                  Likely 90d
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-0.5 leading-snug">{renderReason(b.headline, b.calculator_property)}</p>
            {/* The property this call-back is about (e.g. the home being
                appraised) so a seller card shows more than the owner's
                details — CRM-2026-00085. */}
            {b.property_address && (
              <p className="mt-1 flex items-start gap-1 text-[11px] font-medium text-gray-700">
                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400" />
                <span className="truncate">{b.property_address}</span>
              </p>
            )}
          </div>
          <div className="flex flex-col items-end flex-shrink-0">
            <span className="text-lg font-bold text-gray-800 leading-none">{b.call_score}</span>
            <span className="text-[9px] text-gray-400 uppercase tracking-wide">score</span>
          </div>
        </div>

        {/* Meta row: suburb / price band / investor lean */}
        {(b.top_suburb || b.price_band || b.investor_lean >= 0.5) && (
          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500 flex-wrap">
            {b.top_suburb && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {b.top_suburb}
              </span>
            )}
            {b.price_band && <span>{b.price_band}</span>}
            {b.investor_lean >= 0.5 && (
              <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-[10px] font-medium">
                Investor
              </span>
            )}
          </div>
        )}

        {/* Buyer Intent Score row: score + category, week-over-week
            trend, finance + timeline — the same intelligence as the
            Top Engaged Buyers analytics page. */}
        {(b.intent_score !== null && b.intent_score !== undefined) && (
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${INTENT_CATEGORY_STYLES[b.intent_category] || INTENT_CATEGORY_STYLES.cold}`}
              title={`Buyer Intent Score${b.intent_category ? ` — ${b.intent_category}` : ''}`}
            >
              Intent {b.intent_score}
              {b.intent_category ? ` · ${b.intent_category}` : ''}
            </span>
            <span className="text-[10px]">
              <IntentTrend trend={b.intent_trend || 0} />
            </span>
            {b.finance_label && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                style={{
                  background: `${FINANCE_CHIP_COLORS[b.finance_label] || '#0d9488'}1a`,
                  color: FINANCE_CHIP_COLORS[b.finance_label] || '#0d9488',
                }}
              >
                {b.finance_label}
              </span>
            )}
            {b.timeline_label && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-gray-600">
                <CalendarClock className="w-3 h-3 text-gray-400" />
                {b.timeline_label}
              </span>
            )}
          </div>
        )}

        {/* Reasons */}
        <ul className="mt-2 space-y-1">
          {visibleReasons.map((r, i) => (
            <li key={i} className="flex items-start gap-1.5 text-[11px] text-gray-600">
              <span className="mt-1 w-1 h-1 rounded-full bg-teal-400 flex-shrink-0" />
              <span className="leading-snug">{renderReason(r, r.startsWith('Saved your listing') ? b.saved_listing?.address : b.calculator_property)}</span>
            </li>
          ))}
        </ul>
        {reasons.length > 2 && (
          <button
            onClick={() => onToggle(key)}
            className="mt-1 text-[11px] text-teal-600 font-medium flex items-center gap-0.5 hover:underline"
          >
            {expanded ? (
              <>Less <ChevronUp className="w-3 h-3" /></>
            ) : (
              <>+{reasons.length - 2} more <ChevronDown className="w-3 h-3" /></>
            )}
          </button>
        )}

        {/* Latest open-home feedback — the buyer's own words at an inspection.
            This now feeds the call score above; shown here so the agent opens
            the call with what the buyer actually said. */}
        {(b.latest_feedback?.text || b.latest_feedback?.interest_level) && (
          <div className="mt-2 rounded-md bg-teal-50 border border-teal-100 px-2 py-1.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <MessageSquare className="w-3 h-3 text-teal-500 flex-shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-wide text-teal-700">
                Open-home feedback
              </span>
              {b.latest_feedback.interest_level && (
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${FEEDBACK_CHIP[b.latest_feedback.interest_level] || FEEDBACK_CHIP.warm}`}>
                  {FEEDBACK_LABEL[b.latest_feedback.interest_level] || b.latest_feedback.interest_level}
                </span>
              )}
              {b.latest_feedback.property && (
                <span className="text-[10px] text-gray-500 truncate">{b.latest_feedback.property}</span>
              )}
            </div>
            {b.latest_feedback.text && (
              <p className="text-[11px] text-gray-700 whitespace-pre-wrap break-words mt-1">"{b.latest_feedback.text}"</p>
            )}
            {b.latest_feedback.price_feedback && (
              <p className="text-[10px] text-gray-500 mt-0.5">Price feedback: {b.latest_feedback.price_feedback}</p>
            )}
          </div>
        )}

        {/* Contact details — shown in full so the agent can read/dial them directly */}
        <div className="mt-2.5 flex flex-col gap-1.5 rounded-lg bg-gray-50 px-2.5 py-2">
          {b.phone ? (
            <a
              href={`tel:${b.phone}`}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-800 hover:text-teal-600 transition-colors"
            >
              <PhoneCall className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
              <span className="tabular-nums">{b.phone}</span>
            </a>
          ) : (
            <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
              <PhoneCall className="w-3.5 h-3.5 flex-shrink-0" />
              No phone on file
            </span>
          )}
          {b.email && (
            <a
              href={`mailto:${b.email}`}
              className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-teal-600 transition-colors min-w-0"
            >
              <Mail className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
              <span className="truncate">{b.email}</span>
            </a>
          )}
        </div>

        {/* Actions. Call and Called share the line (DOMA 13353563069): ring
            them, then log it, without the eye dropping to a second row. The
            Called tile is the same one as the property tabs: pick the call
            outcome or set a quick call-back; both log a proper call and drop
            the card off the list (a call-back brings them back in the
            Call-backs section on its date). Call is button-sized and the red
            "Remove from list" sits right after Called (Christian, CRM-2026-00122);
            it drops the card without logging a call. Facebook leads can't be
            removed, they leave once contacted. */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {b.phone ? (
            <a
              href={`tel:${b.phone}`}
              className="flex items-center justify-center gap-1.5 bg-teal-500 hover:bg-teal-600 text-white text-xs font-bold py-1.5 px-3 rounded-lg transition-colors"
            >
              <PhoneCall className="w-3.5 h-3.5" />
              Call
            </a>
          ) : (
            <span className="text-center text-[11px] text-gray-400 py-1.5 px-3 bg-gray-50 rounded-lg">
              No phone on file
            </span>
          )}
          <CalledButton
            lastContacted={b.last_contacted || null}
            busy={busy}
            onCalled={handleCalled}
            size="buyer"
          />
          {!isMetaLeadCard(b) && (confirmRemove ? (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-gray-600">Remove without logging a call?</span>
              <button
                onClick={handleRemove}
                disabled={busy}
                className="px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
              >
                Remove
              </button>
              <button
                onClick={() => setConfirmRemove(false)}
                disabled={busy}
                className="px-2 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmRemove(true)}
              disabled={busy}
              title="Take this person off your list without logging a call or setting a reminder"
              className="flex items-center justify-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold py-1.5 px-3 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-3.5 h-3.5" />
              Remove from list
            </button>
          ))}
          {b.email && (
            <a
              href={`mailto:${b.email}`}
              className="flex items-center justify-center gap-1.5 border border-gray-200 hover:border-teal-300 hover:text-teal-600 text-gray-600 text-xs font-medium py-1.5 px-3 rounded-lg transition-colors"
              title={b.email}
            >
              <Mail className="w-3.5 h-3.5" />
              Email
            </a>
          )}
          <ContactNotesButton
            suggestPreferences
            identity={identity}
            className="flex items-center justify-center gap-1.5 border border-gray-200 hover:border-amber-300 hover:text-amber-600 text-gray-600 text-xs font-medium py-1.5 px-3 rounded-lg transition-colors"
            onNoteChange={(ln) => onNoteChange(key, ln)}
          />
          <button
            onClick={openPrefs}
            title="Edit this buyer's buying preferences"
            className="flex items-center justify-center gap-1.5 border border-gray-200 hover:border-teal-300 hover:text-teal-600 text-gray-600 text-xs font-medium py-1.5 px-3 rounded-lg transition-colors"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Preferences</span>
          </button>
        </div>

        {/* Most recent note — shown inline so the agent sees the latest
            context, and a note just saved appears here instantly. */}
        {b.latest_note?.text && (
          <div className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-100 px-2 py-1.5">
            <StickyNote className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] text-gray-700 whitespace-pre-wrap break-words">{b.latest_note.text}</p>
              {(b.latest_note.author || b.latest_note.created_at) && (
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {b.latest_note.author}
                  {b.latest_note.author && b.latest_note.created_at ? ' · ' : ''}
                  {b.latest_note.created_at
                    ? new Date(b.latest_note.created_at).toLocaleString('en-AU', {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })
                    : ''}
                </p>
              )}
            </div>
          </div>
        )}

        {removeError && (
          <p className="mt-1 text-[11px] text-red-500">Couldn't remove this card. Please try again.</p>
        )}
      </div>

      {/* Edit buying preferences — full portal-mirror editor in a modal. */}
      {prefsOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={() => setPrefsOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 min-w-0">
                <SlidersHorizontal className="w-4 h-4 text-teal-600 shrink-0" />
                <span className="text-sm font-semibold text-gray-900 truncate">Buying preferences · {b.name}</span>
              </div>
              <button onClick={() => setPrefsOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Status + quick actions, so an agent can change this buyer's status
                without leaving the preferences modal (CRM-2026-00097). */}
            <BuyerStatusActions ref={statusRef} buyer={identity} />
            <div className="flex-1 overflow-y-auto">
              {resolvingContact ? (
                <div className="flex items-center gap-2 justify-center py-16 text-sm text-gray-500">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Opening preferences…
                </div>
              ) : contactId ? (
                <ContactBuyerPreferencesEditor
                  contactId={contactId}
                  beforeSave={() => statusRef.current?.savePending() ?? true}
                  onSaved={() => setPrefsOpen(false)}
                />
              ) : (
                <div className="py-16 text-center text-sm text-gray-500">
                  Couldn't open this buyer's preferences. Please try again.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CallListSettingRow = ({ label, hint, checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    disabled={disabled}
    className="w-full flex items-start gap-2.5 p-2 rounded-lg hover:bg-gray-50 text-left disabled:opacity-60"
  >
    <span
      className={`mt-0.5 w-8 h-[18px] rounded-full flex-shrink-0 transition-colors ${
        checked ? 'bg-teal-500' : 'bg-gray-300'
      }`}
    >
      <span
        className={`block w-3.5 h-3.5 mt-[2px] bg-white rounded-full shadow transition-transform ${
          checked ? 'translate-x-[16px]' : 'translate-x-[2px]'
        }`}
      />
    </span>
    <span className="min-w-0">
      <span className="block text-xs font-semibold text-gray-800">{label}</span>
      <span className="block text-[11px] text-gray-500 leading-snug">{hint}</span>
    </span>
  </button>
);

/**
 * "My call list settings" popover.
 *
 * Teams are set up differently: on some, only the lead (first) agent works the
 * buyer calls, on others the second agent does all the calling. Each agent
 * picks which of their listings put buyers on their own list. Both switches off
 * means no listing buyers at all, though referrals, call-back reminders and the
 * agent's own open home attendees still show.
 */
const CallListSettings = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || settings) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchCallListSettings()
      .then((res) => { if (!cancelled) setSettings(res); })
      .catch(() => { if (!cancelled) setError("Couldn't load your settings."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, settings]);

  // Close when clicking anywhere outside. Each switch saves as it is flipped, so
  // there is never unsaved work to lose here.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (!e.target.closest || !e.target.closest('[data-call-list-settings]')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const save = async (patch) => {
    setSaving(true);
    setError('');
    // Optimistic: flip the switch straight away so it feels instant, and put it
    // back if the save fails.
    const previous = settings;
    setSettings((cur) => (cur ? { ...cur, ...patch, is_default: false } : cur));
    try {
      const res = await updateCallListSettings({
        leadListings: patch.lead_listings,
        secondListings: patch.second_listings,
      });
      setSettings(res);
      if (onSaved) onSaved();
    } catch (e) {
      setSettings(previous);
      setError("Couldn't save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="relative" data-call-list-settings>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`transition-colors flex-shrink-0 ${
          open ? 'text-teal-600' : 'text-gray-400 hover:text-teal-600'
        }`}
        title="My call list settings"
      >
        <Settings className="w-4 h-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-6 z-30 w-72 bg-white rounded-xl shadow-lg border border-gray-200 p-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="text-xs font-bold text-gray-800">My call list settings</p>
              <p className="text-[11px] text-gray-500 leading-snug">
                Which of your listings put buyers on your list
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-gray-600"
              title="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {loading ? (
            <div className="py-6 text-center text-[11px] text-gray-400">Loading...</div>
          ) : settings ? (
            <>
              <CallListSettingRow
                label="Listings where I'm the lead agent"
                hint="Buyers active on the listings you are first contact on"
                checked={!!settings.lead_listings}
                onChange={(v) => save({ lead_listings: v })}
                disabled={saving}
              />
              <CallListSettingRow
                label="Listings where I'm the second agent"
                hint="Turn this on if you do the calling on your team's listings"
                checked={!!settings.second_listings}
                onChange={(v) => save({ second_listings: v })}
                disabled={saving}
              />
              {settings.is_default && (
                <p className="mt-1 px-2 text-[10px] text-gray-400 leading-snug">
                  Currently automatic: you get your own listings, plus any where
                  you are second agent and there is no active lead agent.
                </p>
              )}
              {!settings.is_default
                && !settings.lead_listings
                && !settings.second_listings && (
                <p className="mt-1 px-2 text-[10px] text-amber-600 leading-snug">
                  No listing buyers will show. Referrals, call-back reminders and
                  your own open home attendees still do.
                </p>
              )}
            </>
          ) : null}

          {error && <p className="mt-1 px-2 text-[10px] text-red-500">{error}</p>}
        </div>
      )}
    </div>
  );
};

// `title` only re-labels the heading. The redesigned dashboard calls this panel
// "Priority Today" (Christian Gravias, Monday 12988442847) because that is what
// the approved design names it; everywhere else it is still Who to Call, which
// is the name the call-list settings and the rest of the product use.
const WhoToCallPanel = ({ title = 'Who to Call' }) => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState({});
  // Preview a handful of cards by default; "View all" expands into a
  // scrollable list so the dashboard stays short and easy to scroll past.
  const [showAll, setShowAll] = useState(false);
  const PREVIEW_COUNT = 5;
  // Which list the admin is viewing. 'active' = all active portal buyers
  // (office-wide, freshest); 'unassigned' = the residual pool no agent owns
  // (structurally colder). Admins default to the fresh list — the unassigned
  // pool reads stale because hot buyers get attributed to their agent and
  // skimmed off it. Non-admins ignore this (their own list is the agent default).
  const [perspective, setPerspective] = useState('active');
  // Buyer-enquiry vs potential-seller filter (CRM-2026-00012). Client-side: the
  // whole list is already fetched and each card carries a `category`.
  const [category, setCategory] = useState('all');
  // Free-text search over the loaded list — name/email/phone/suburb. Client-side,
  // so it's instant and works across all sections. Handy for confirming whether a
  // specific buyer is (still) on the list, e.g. after they've purchased.
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // Only admins choose a perspective; agents always get their own list.
      const res = await fetchSmartCallList(12, 30, isAdmin ? perspective : undefined);
      setData(res);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, perspective]);

  useEffect(() => {
    load();
  }, [load]);

  const allBuyers = data?.buyers || [];
  // Fallback to 'buyer' for any card without a category, so the filter is safe
  // regardless of FE/BE deploy order.
  const sellerCount = allBuyers.filter((b) => (b.category || 'buyer') === 'seller').length;
  const buyerCount = allBuyers.length - sellerCount;
  const inCategory = category === 'all'
    ? allBuyers
    : allBuyers.filter((b) => (b.category || 'buyer') === category);
  // Apply the free-text search on top of the category filter.
  const q = query.trim().toLowerCase();
  const buyers = q
    ? inCategory.filter((b) =>
        [b.name, b.email, b.phone, b.top_suburb, b.headline]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q)
      )
    : inCategory;
  const view = data?.perspective;
  const fallback = data?.fallback;
  const subtitle = view === 'active'
    ? 'Active portal buyers across the office — ranked by recent activity'
    : view === 'admin'
    ? 'Unassigned portal buyers — no agent attached yet'
    : fallback === 'portal_activity'
    ? 'No buyers on your listings yet — showing active portal buyers'
    : fallback === 'random'
    ? 'No recent activity yet — buyers to introduce yourself to'
    : 'Buyers on your listings & referrals, ranked by portal activity';

  // Reminder-driven cards (a call-back you scheduled, now due) get their own
  // section so a buyer you've already contacted resurfaces here on the date you
  // set, instead of being lost among the activity-ranked list.
  // Meta (Facebook) leads lead the whole list and are never trimmed by the
  // preview: every uncontacted one is urgent.
  const metaLeadCards = buyers.filter(isMetaLeadCard);
  const reminderCards = buyers.filter(isReminderCard);
  const openHomeCards = buyers.filter(isOpenHomeCard).sort(byOpenHomeInterest);
  const activityCards = buyers.filter(
    (b) => !isMetaLeadCard(b) && !isReminderCard(b) && !isOpenHomeCard(b)
  );

  // In preview mode, show Meta leads (all of them), then reminders, then open-home
  // interest, then fill the rest of the preview budget with activity cards; "View
  // all" reveals every card. An active search also reveals every match, so nothing
  // hides behind the preview.
  const showEverything = showAll || !!q;
  const reminderBudget = Math.max(0, PREVIEW_COUNT - metaLeadCards.length);
  const visibleReminderCards = showEverything ? reminderCards : reminderCards.slice(0, reminderBudget);
  const openHomeBudget = Math.max(0, reminderBudget - visibleReminderCards.length);
  const visibleOpenHomeCards = showEverything ? openHomeCards : openHomeCards.slice(0, openHomeBudget);
  const activityBudget = Math.max(
    0, openHomeBudget - visibleOpenHomeCards.length
  );
  const visibleActivityCards = showEverything ? activityCards : activityCards.slice(0, activityBudget);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  // A note added/deleted via the card's Notes popover updates the inline note
  // on that card immediately, without waiting for a reload.
  const handleNoteChange = useCallback((key, latestNote) => {
    setData((d) =>
      d
        ? { ...d, buyers: (d.buyers || []).map((x) => (cardKey(x) === key ? { ...x, latest_note: latestNote } : x)) }
        : d
    );
  }, []);

  // Agent has phoned/contacted this person — drop the card off the list.
  const removeCard = useCallback((key) => {
    setData((d) =>
      d ? { ...d, buyers: (d.buyers || []).filter((x) => cardKey(x) !== key) } : d
    );
  }, []);

  const renderCard = (b) => (
    <CallCard
      key={cardKey(b)}
      b={b}
      expanded={!!expanded[cardKey(b)]}
      onToggle={toggle}
      onNoteChange={handleNoteChange}
      onContacted={removeCard}
    />
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
      <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <div className="mt-0.5 p-1.5 bg-teal-100 rounded-lg flex-shrink-0">
            <Sparkles className="w-4 h-4 text-teal-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm sm:text-base font-bold text-gray-800 flex items-center gap-2">
              {title}
              {buyers.length > 0 && (
                <span className="px-1.5 py-0.5 bg-teal-50 text-teal-600 text-[10px] rounded font-bold">
                  {buyers.length}
                </span>
              )}
            </h2>
            <p className="text-[11px] sm:text-xs text-gray-500 truncate">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Agents choose which of their listings feed this list. Admins are
              looking at the office wide lists below, where it does not apply. */}
          {!isAdmin && <CallListSettings onSaved={load} />}
          <button
            onClick={load}
            disabled={loading}
            className="text-gray-400 hover:text-teal-600 transition-colors disabled:opacity-50"
            title="Refresh call list"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Buyer enquiries vs potential sellers (CRM-2026-00012). Buyer enquiries
          are portal/open-home buyers and buyer call-backs; potential sellers are
          the call-backs you set on seller/vendor contacts. Everyone sees this. */}
      <div className="flex items-center gap-1 mb-3 p-0.5 bg-gray-100 rounded-lg w-fit">
        {[
          { key: 'all', label: 'All' },
          { key: 'buyer', label: `Buyer enquiries${buyerCount ? ` (${buyerCount})` : ''}` },
          { key: 'seller', label: `Potential sellers${sellerCount ? ` (${sellerCount})` : ''}` },
        ].map((opt) => (
          <button
            key={opt.key}
            onClick={() => setCategory(opt.key)}
            disabled={loading}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors disabled:opacity-50 ${
              category === opt.key
                ? 'bg-white text-teal-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Admin list switcher: flip between the freshest office-wide active
          buyers and the residual unassigned pool. Agents don't see this —
          they always get their own listing/referral list. */}
      {isAdmin && (
        <div className="flex items-center gap-1 mb-3 p-0.5 bg-gray-100 rounded-lg w-fit">
          {[
            { key: 'active', label: 'Active buyers' },
            { key: 'unassigned', label: 'Unassigned' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setPerspective(opt.key)}
              disabled={loading}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors disabled:opacity-50 ${
                perspective === opt.key
                  ? 'bg-white text-teal-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Search the loaded list by name, email, phone or suburb. Client-side and
          instant; also useful to confirm a buyer has dropped off (e.g. purchased). */}
      <div className="relative mb-3">
        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, phone or suburb"
          className="w-full pl-8 pr-8 py-1.5 text-xs rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-teal-300 focus:ring-1 focus:ring-teal-200 outline-none placeholder:text-gray-400"
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Relative wrapper so a reload that keeps the old data on screen (switching
          Active ⇄ Unassigned, or hitting refresh) can show an obvious dimmed
          spinner overlay instead of silently swapping the list underneath. */}
      <div className="relative">
        {loading && data && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-[1px]">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-teal-600 shadow-sm ring-1 ring-teal-100">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Loading…
            </span>
          </div>
        )}

        {loading && !data ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 bg-gray-50 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="py-8 text-center text-gray-500 text-sm">
          Couldn't load the call list.{' '}
          <button onClick={load} className="text-teal-600 font-medium hover:underline">
            Retry
          </button>
        </div>
      ) : buyers.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm">
          <PhoneCall className="w-10 h-10 mx-auto mb-2 stroke-1 text-gray-300" />
          {q ? (
            <>No one on the call list matches &ldquo;{query.trim()}&rdquo;.</>
          ) : (
            'No active buyers to call right now.'
          )}
        </div>
      ) : (
        <div className={`space-y-3 overflow-y-auto no-scrollbar pr-1 transition-opacity ${showEverything ? 'max-h-[600px]' : 'max-h-[300px]'} ${loading ? 'opacity-40' : ''}`}>
          {/* New Facebook leads nobody has phoned yet, always first. */}
          {metaLeadCards.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-blue-600">
                <Zap className="w-3.5 h-3.5" />
                New Facebook leads, call first
                <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-bold">
                  {metaLeadCards.length}
                </span>
              </div>
              {metaLeadCards.map(renderCard)}
            </div>
          )}

          {/* Call-backs due — reminders you set, now resurfacing on their date. */}
          {visibleReminderCards.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-600">
                <BellRing className="w-3.5 h-3.5" />
                Needs a call back
                <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded font-bold">
                  {reminderCards.length}
                </span>
              </div>
              {visibleReminderCards.map(renderCard)}
            </div>
          )}

          {/* From your open homes — attendees you marked hot/warm, or left
              feedback with, at an inspection. These don't need a portal account. */}
          {visibleOpenHomeCards.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-rose-600">
                <Flame className="w-3.5 h-3.5" />
                Interested at your open homes
                <span className="px-1.5 py-0.5 bg-rose-50 text-rose-600 rounded font-bold">
                  {openHomeCards.length}
                </span>
              </div>
              {visibleOpenHomeCards.map(renderCard)}
            </div>
          )}

          {/* Activity-ranked list. */}
          {visibleActivityCards.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                <Sparkles className="w-3.5 h-3.5" />
                Active on the portal
              </div>
              {visibleActivityCards.map(renderCard)}
            </div>
          )}
        </div>
      )}

      {/* Preview toggle — kept outside the scroll window so it's always visible.
          Hidden while searching, since a search already reveals every match. */}
      {!loading && !error && !q && buyers.length > PREVIEW_COUNT && (
        <button
          onClick={() => setShowAll((s) => !s)}
          className="mt-2 w-full text-center text-xs font-semibold text-teal-600 hover:text-teal-700 py-2 hover:underline"
        >
          {showAll ? 'Show less' : `View all ${buyers.length}`}
        </button>
      )}
      </div>
    </div>
  );
};

export default WhoToCallPanel;
